/**
 * 4.6 §4.3 Campus 미션(Field 판정 — P2-6) 제어 평면 계약(C-07 · Bearer · 학생 본인 것만):
 *   GET /api/courses/{c}/mission → {mission: null | {expiresAt, goals:[{key, title, modeId, presetKey, timeUnderMs, state, timeMs}]}}
 *       state = open(아직) · pending(확인 중) · verified(확인됨 — timeMs 는 그 기록 · 본인만) · failed(확인하지 못함)
 *   POST /api/courses/{c}/attempts → 201 {attemptId, verification: null | {status:"pending", missionKeys}}(socialApi.postAttempt)
 *   GET /api/courses/{c}/attempts/{attemptId}/verification → {status: none|pending|verified|mismatch|failed, missionKeys, timeMs}
 *   POST /api/courses/{c}/mission/playground {evidence} → {missionKeys}(micro:bit 놀이터 — 본 것 달성 · 모드 `microbit-playground` · V18)
 * 미션 묶음 id(tid)는 브라우저에 오지 않는다. 제목은 Campus 저작 문구(개인 정보 아님). 남의 시간 · 순위는 없다(SR-ACH-01 · DR-01).
 */
import { useMemo } from "react";
import { useSession, useSessionManager } from "../../store/session/SessionProvider";
import type { Requester } from "../../types/api";

export type GoalState = "open" | "pending" | "verified" | "failed";
export type VerificationStatus = "none" | "pending" | "verified" | "mismatch" | "failed";

export interface MissionGoal {
  key: string;
  title: string;
  modeId: string;
  presetKey: string;
  /** 시간 목표(ms) — 없으면 완주만 */
  timeUnderMs: number | null;
  state: GoalState;
  /** 확인된 내 기록(ms — 본인만) */
  timeMs: number | null;
}

export interface Mission {
  expiresAt: string | null;
  goals: MissionGoal[];
}

export interface AttemptVerification {
  status: VerificationStatus;
  missionKeys: string[];
  timeMs: number | null;
}

/** 도전 기록 올리기 응답의 검증 대기(없으면 미션과 무관한 판) */
export interface AttemptPosted {
  attemptId: string | null;
  verification: { status: VerificationStatus; missionKeys: string[] } | null;
}

/** micro:bit 놀이터 미션 모드(Campus '필드: 놀이터 …' · 제어 평면 PlaygroundMissionService.MODE_ID) */
export const PLAYGROUND_MODE = "microbit-playground";

/** 놀이터가 본 것(증거) — 서버가 같은 목표 규칙으로 다시 따진다. 아이콘은 micro:bit 아이콘 이름(lib icons.ts) */
export interface PlaygroundEvidence {
  icons: string[];
  pressedA: boolean;
  pressedB: boolean;
  stars: number;
  /** 놀이를 시작한 뒤 지난 시간(ms) */
  elapsedMs: number;
  /** 차시 장면(PICNIC-MB-FIELD-001 §6.3) — 표정 리모컨 코스 결승 · 걸음 · 박동 · 반복 상영한 그림 수 */
  courseDone?: boolean;
  steps?: number;
  beats?: number;
  movieFrames?: number;
  /**
   * 해낸 차시 장면(PICNIC-MB-FIELD-001 §6.13 · 결정 '나') — 놀이 시작부터 끝난 때(ms · 3초 넘게) · 채운 수 · 목표 수.
   * 서버는 형식 · 시간 · 순서로만 확인하고 프리셋 `scene:<차시>` 목표를 채운다
   */
  scenes?: { lesson: string; doneAtMs: number; n: number; goal: number }[];
}

export interface MissionApi {
  /** 묶인 미션(없으면 null) — 실패해도 던지지 않고 null(미션 없이도 놀이는 된다) */
  mission(): Promise<Mission | null>;
  verification(attemptId: string): Promise<AttemptVerification>;
  /** 놀이터 증거 보내기 → 이번에 달성한 미션 키(없으면 빈 목록) */
  playground(evidence: PlaygroundEvidence): Promise<string[]>;
}

const GOAL_STATES: readonly GoalState[] = ["open", "pending", "verified", "failed"];
const V_STATUSES: readonly VerificationStatus[] = [
  "none",
  "pending",
  "verified",
  "mismatch",
  "failed",
];
const num = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null;
const keys = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

/** 계약 밖 필드는 버린다(남의 시간 · 순위 같은 것이 섞여 와도 그리지 않게) · 목표 ≤ 8(4.6 §7) */
export function readMission(v: unknown): Mission | null {
  const m = (v as { mission?: unknown } | null)?.mission;
  if (!m || typeof m !== "object") return null;
  const o = m as Record<string, unknown>;
  const goals = (Array.isArray(o.goals) ? o.goals : [])
    .map((g) => (g ?? {}) as Record<string, unknown>)
    .filter((g) => typeof g.key === "string" && typeof g.presetKey === "string")
    .slice(0, 8)
    .map((g): MissionGoal => ({
      key: g.key as string,
      title: typeof g.title === "string" ? g.title.slice(0, 40) : "",
      modeId: typeof g.modeId === "string" ? g.modeId : "",
      presetKey: g.presetKey as string,
      timeUnderMs: num(g.timeUnderMs),
      state: GOAL_STATES.includes(g.state as GoalState) ? (g.state as GoalState) : "open",
      timeMs: num(g.timeMs),
    }));
  if (!goals.length) return null;
  return { expiresAt: typeof o.expiresAt === "string" ? o.expiresAt : null, goals };
}

export function readVerification(v: unknown): AttemptVerification {
  const o = (v ?? {}) as Record<string, unknown>;
  return {
    status: V_STATUSES.includes(o.status as VerificationStatus)
      ? (o.status as VerificationStatus)
      : "none",
    missionKeys: keys(o.missionKeys),
    timeMs: num(o.timeMs),
  };
}

export function readAttemptPosted(v: unknown): AttemptPosted {
  const o = (v ?? {}) as Record<string, unknown>;
  const ver = o.verification as Record<string, unknown> | null | undefined;
  return {
    attemptId: typeof o.attemptId === "string" ? o.attemptId : null,
    verification:
      ver && typeof ver === "object"
        ? {
            status: V_STATUSES.includes(ver.status as VerificationStatus)
              ? (ver.status as VerificationStatus)
              : "pending",
            missionKeys: keys(ver.missionKeys),
          }
        : null,
  };
}

export function createMissionApi(request: Requester, courseId: string): MissionApi {
  const c = `/api/courses/${encodeURIComponent(courseId)}`;
  return {
    async mission() {
      try {
        return readMission(await request<unknown>(`${c}/mission`));
      } catch {
        return null;
      }
    },
    async verification(attemptId) {
      return readVerification(
        await request<unknown>(`${c}/attempts/${encodeURIComponent(attemptId)}/verification`),
      );
    },
    async playground(evidence) {
      const r = await request<unknown>(`${c}/mission/playground`, {
        method: "POST",
        body: JSON.stringify({ evidence }),
      });
      return keys((r as { missionKeys?: unknown } | null)?.missionKeys);
    },
  };
}

export function useMissionApi(): MissionApi | null {
  const manager = useSessionManager();
  const state = useSession();
  const student = state.status === "ready" && state.session.role === "student";
  const courseId = state.status === "ready" ? state.session.courseId : null;
  return useMemo(
    () => (courseId && student ? createMissionApi(manager.request, courseId) : null),
    [manager, courseId, student],
  );
}
